// Notification Utilities for Push Notifications
import { LocalNotifications } from '@capacitor/local-notifications';

/**
 * Request notification permission from the user (works on Web and Android)
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission | 'granted' | 'denied'> => {
  // Check if running on Capacitor (Android/iOS)
  if (typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform()) {
    try {
      const result = await LocalNotifications.requestPermissions();
      console.log('Android notification permission:', result);
      return result.display === 'granted' ? 'granted' : 'denied';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  // Browser notifications
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }

  return Notification.permission;
};

/**
 * Register service worker for push notifications
 */
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }
  return null;
};

/**
 * Show a local notification (works on Web and Android)
 */
export const showLocalNotification = async (title: string, body: string, url?: string) => {
  // Check if running on Capacitor (Android/iOS)
  if (typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform()) {
    try {
      await LocalNotifications.schedule({
        notifications: [
          {
            title,
            body,
            id: Date.now(),
            schedule: { at: new Date(Date.now() + 1000) }, // Show in 1 second
            sound: 'default',
            attachments: [],
            actionTypeId: '',
            extra: { url: url || '/home' }
          }
        ]
      });
      console.log('Android notification scheduled');
    } catch (error) {
      console.error('Error showing Android notification:', error);
    }
    return;
  }

  // Browser notifications
  if (Notification.permission === 'granted') {
    const notificationOptions: any = {
      body,
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: 'local-notification',
      requireInteraction: false
    };
    
    // Add vibrate if supported (works on mobile)
    if ('vibrate' in navigator) {
      notificationOptions.vibrate = [200, 100, 200];
    }
    
    const notification = new Notification(title, notificationOptions);

    notification.onclick = () => {
      window.focus();
      if (url) {
        window.location.href = url;
      }
      notification.close();
    };
  }
};

/**
 * Schedule a daily reminder notification
 * @param hour - Hour in 24-hour format (0-23) in USER'S LOCAL TIME
 * @param minute - Minute (0-59) in USER'S LOCAL TIME
 * 
 * Note: Uses user's local timezone, NOT Italian time.
 * If user sets 20:00, they'll get notification at 8 PM in their timezone.
 */
export const scheduleDailyReminder = async (hour: number, minute: number) => {
  // Store reminder time in localStorage
  const reminderTime = { hour, minute, enabled: true };
  localStorage.setItem('dailyReminderTime', JSON.stringify(reminderTime));

  // Check if running on native platform (Android/iOS)
  if (typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform()) {
    await scheduleNativeNotifications(hour, minute);
  } else {
    // For web, use the old setTimeout method (less reliable)
    checkAndScheduleReminder();
  }
};

/**
 * Schedule native notifications using Capacitor
 * This works when app is closed and after reboot
 */
const scheduleNativeNotifications = async (hour: number, minute: number) => {
  try {
    // Get user's language preference
    const lang = localStorage.getItem('preferredLanguage') || 'en';
    
    const messages = {
      bn: {
        title: 'ইতালিয়ান শেখার সময়! 🇮🇹',
        body: 'আপনার দৈনিক অনুশীলন ভুলবেন না!'
      },
      it: {
        title: 'È ora di imparare l\'italiano! 🇮🇹',
        body: 'Non dimenticare la tua pratica quotidiana!'
      },
      en: {
        title: 'Time to Learn Italian! 🇮🇹',
        body: 'Don\'t forget your daily practice!'
      }
    };
    
    const message = messages[lang as keyof typeof messages] || messages.en;

    // Cancel any existing notifications
    await LocalNotifications.cancel({ notifications: [{ id: 1 }] });

    const now = new Date();
    
    // Schedule for the next 7 days to ensure notifications work
    for (let i = 0; i < 7; i++) {
      const notificationTime = new Date();
      notificationTime.setDate(now.getDate() + i);
      notificationTime.setHours(hour, minute, 0, 0);
      
      // Skip today if time already passed
      if (i === 0 && now > notificationTime) {
        continue; // First notification is today but already passed
      }
      
      await LocalNotifications.schedule({
        notifications: [{
          id: i + 1,
          title: message.title,
          body: message.body,
          // allowWhileIdle lets the reminder fire during Doze without needing the
          // restricted exact-alarm permission. Delivery may drift by a few minutes.
          schedule: { at: notificationTime, allowWhileIdle: true },
          sound: 'default',
          actionTypeId: 'OPEN_APP',
          extra: { url: '/home' }
        }]
      });
      
      console.log(`Scheduled notification ${i + 1} for ${notificationTime.toLocaleString()}`);
    }
    
    console.log('✅ Native notifications scheduled successfully');
  } catch (error) {
    console.error('Error scheduling native notifications:', error);
  }
};

/**
 * Check if it's time to show reminder and schedule next check
 */
export const checkAndScheduleReminder = () => {
  const reminderData = localStorage.getItem('dailyReminderTime');
  if (!reminderData) return;

  try {
    const { hour, minute, enabled } = JSON.parse(reminderData);
    if (!enabled) return;

    const now = new Date();
    const reminderTime = new Date();
    reminderTime.setHours(hour, minute, 0, 0);

    // If reminder time has passed today, schedule for tomorrow
    if (now > reminderTime) {
      reminderTime.setDate(reminderTime.getDate() + 1);
    }

    const timeUntilReminder = reminderTime.getTime() - now.getTime();

    // Schedule the notification
    setTimeout(() => {
      const reminderEnabled = localStorage.getItem('reminderEnabled');
      if (reminderEnabled === 'true' || reminderEnabled === null) {
        // Get user's language preference
        const lang = localStorage.getItem('preferredLanguage') || 'en';
        
        const messages = {
          bn: {
            title: 'ইতালিয়ান শেখার সময়! 🇮🇹',
            body: 'আপনার দৈনিক অনুশীলন ভুলবেন না!'
          },
          it: {
            title: 'È ora di imparare l\'italiano! 🇮🇹',
            body: 'Non dimenticare la tua pratica quotidiana!'
          },
          en: {
            title: 'Time to Learn Italian! 🇮🇹',
            body: 'Don\'t forget your daily practice!'
          }
        };

        const message = messages[lang as keyof typeof messages] || messages.en;
        showLocalNotification(message.title, message.body, '/home');
      }

      // Schedule next reminder (24 hours later)
      checkAndScheduleReminder();
    }, timeUntilReminder);

    console.log(`Reminder scheduled for ${reminderTime.toLocaleString()}`);
  } catch (error) {
    console.error('Error scheduling reminder:', error);
  }
};

/**
 * Disable daily reminders
 */
export const disableDailyReminder = async () => {
  localStorage.setItem('reminderEnabled', 'false');
  
  // Cancel native notifications if on Android/iOS
  if (typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform()) {
    try {
      await LocalNotifications.cancel({ 
        notifications: [{ id: 1 }, { id: 2 }, { id: 3 }, { id: 4 }, { id: 5 }, { id: 6 }, { id: 7 }]
      });
      console.log('✅ Native notifications cancelled');
    } catch (error) {
      console.error('Error cancelling notifications:', error);
    }
  }
};

/**
 * Enable daily reminders
 */
export const enableDailyReminder = async () => {
  localStorage.setItem('reminderEnabled', 'true');
  
  // Get saved reminder time and schedule
  const reminderData = localStorage.getItem('dailyReminderTime');
  if (reminderData) {
    try {
      const { hour, minute } = JSON.parse(reminderData);
      await scheduleDailyReminder(hour, minute);
    } catch (error) {
      console.error('Error scheduling reminder:', error);
    }
  } else {
    // Fallback to setTimeout for web
    checkAndScheduleReminder();
  }
};

/**
 * Check if reminders are enabled
 */
export const areRemindersEnabled = (): boolean => {
  const enabled = localStorage.getItem('reminderEnabled');
  return enabled === 'true' || enabled === null; // Default to true
};

/**
 * Send a test notification
 */
export const sendTestNotification = () => {
  const lang = localStorage.getItem('preferredLanguage') || 'en';
  
  const messages = {
    bn: {
      title: 'পরীক্ষা বিজ্ঞপ্তি 🔔',
      body: 'আপনার বিজ্ঞপ্তি কাজ করছে!'
    },
    it: {
      title: 'Notifica di Test 🔔',
      body: 'Le tue notifiche funzionano!'
    },
    en: {
      title: 'Test Notification 🔔',
      body: 'Your notifications are working!'
    }
  };

  const message = messages[lang as keyof typeof messages] || messages.en;
  showLocalNotification(message.title, message.body);
};

/**
 * Initialize notifications on app load
 */
export const initializeNotifications = async () => {
  // Register service worker
  await registerServiceWorker();

  // Check and schedule reminder if enabled
  if (areRemindersEnabled()) {
    checkAndScheduleReminder();
  }
};

